/**
 * Results Admin
 * First feature: freeze a season's results to PDF once the AGM has
 * approved them — a deliberate admin action, separate from the calendar's
 * AGM "confirm season" toggle, so published results are pinned down.
 * Auth: magic-link email → session token stored in cookie (same pattern as
 * Calendar Editor / Secretary Portal).
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';

const API = '/api/results-admin';
// Matches jasper_archive.LAST_JASPER_YEAR in the backend.
const LAST_JASPER_YEAR = 2026;

const setCookie = (name, value, days = 90) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

const S = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" },
  wrap: { maxWidth: 1000, margin: '0 auto', padding: '0 1.5rem 2rem' },
  header: { background: 'linear-gradient(135deg,#1b3d6d,#0f2847)', color: 'white', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' },
  headerTitle: { margin: 0, fontSize: '1.3rem', fontWeight: 700 },
  headerMeta: { fontSize: '0.85rem', opacity: 0.8 },
  btn: { padding: '0.5rem 1.1rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  btnPrimary: { background: '#fb6107', color: 'white' },
  btnSecondary: { background: '#e0e4ea', color: '#333' },
  btnSmall: { padding: '0.3rem 0.7rem', fontSize: '0.8rem', borderRadius: 5, border: 'none', cursor: 'pointer', fontWeight: 600 },
  card: { background: 'white', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem 1.5rem', marginTop: '1rem' },
  input: { width: '100%', padding: '0.42rem 0.6rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem', boxSizing: 'border-box' },
  label: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#555', marginBottom: 3, marginTop: 10 },
  error: { background: '#fdecea', color: '#c0392b', padding: '0.75rem 1rem', borderRadius: 8, margin: '0.5rem 0', fontSize: '0.9rem' },
  success: { background: '#eafaf1', color: '#1e8449', padding: '0.75rem 1rem', borderRadius: 8, margin: '0.5rem 0', fontSize: '0.9rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', background: '#f5f7fa', borderBottom: '2px solid #e0e4ea', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #eef0f3', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 },
};

function LoginScreen() {
  const [email, setEmail] = useState(() => getCookie('results_admin_saved_email') || '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setCookie('results_admin_saved_email', email.trim().toLowerCase(), 365);
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) setError((await res.json()).error || 'Request failed');
      else setSent(true);
    } catch { setError('Network error – please try again'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', padding: '2rem', width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem' }}>🏁</div>
          <h1 style={{ margin: '0.5rem 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#1b3d6d' }}>NHCA Results Admin</h1>
          <p style={{ color: '#888', margin: 0, fontSize: '0.9rem' }}>National Hillclimb Association</p>
        </div>
        {sent ? (
          <div style={S.success}>Check your email for a login link. Valid for 24 hours, single use.</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div style={S.error}>{error}</div>}
            <label style={S.label}>Email address</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com" required autoFocus />
            <button style={{ ...S.btn, ...S.btnPrimary, width: '100%', marginTop: 16, padding: '0.75rem' }}
              type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send Login Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SeasonDocuments({ year, sessionToken }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetch(`${API}/seasons/${year}/documents`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => r.json())
      .then(data => Array.isArray(data) ? setDocs(data) : setError(data.error || 'Failed to load documents'))
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }, [year, sessionToken]);

  if (loading) return <div style={{ padding: '0.75rem 0', color: '#888' }}>Loading documents…</div>;
  if (error) return <div style={S.error}>{error}</div>;
  if (docs.length === 0) return <div style={{ padding: '0.75rem 0', color: '#888' }}>No documents frozen yet.</div>;

  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Type</th>
          <th style={S.th}>Label</th>
          <th style={S.th}>Frozen</th>
          <th style={S.th}>By</th>
          <th style={S.th}>Status</th>
          <th style={S.th}></th>
        </tr>
      </thead>
      <tbody>
        {docs.map(d => (
          <tr key={d.id} style={d.superseded ? { opacity: 0.55 } : undefined}>
            <td style={S.td}>{d.doc_type === 'season-standings' ? 'Standings' : 'Event'}</td>
            <td style={S.td}>{d.label}</td>
            <td style={S.td}>{fmtDateTime(d.created_at)}</td>
            <td style={S.td}>{d.frozen_by}</td>
            <td style={S.td}>
              <span style={{ ...S.badge, background: d.superseded ? '#e0e4ea' : '#eafaf1', color: d.superseded ? '#666' : '#1e8449' }}>
                {d.superseded ? 'Superseded' : 'Current'}
              </span>
            </td>
            <td style={S.td}>
              <a href={`/api/results/archive/${d.id}`} target="_blank" rel="noopener noreferrer">Download</a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LegacyArchiveManager({ sessionToken }) {
  const [year, setYear] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFilename, setUploadFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const filenameInputRef = useRef(null);

  // Pre-fills the filename field rather than requiring it to be typed by
  // hand — "Replace" reuses the exact existing name (so the upload
  // overwrites it); "Add" derives a companion name from it (so the upload
  // lands as a genuinely new, additional file for the same event instead
  // of colliding with — and silently replacing — the existing one).
  function startReplace(existing) {
    setUploadFilename(existing);
    filenameInputRef.current?.focus();
    filenameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function startAddCompanion(existing) {
    const base = existing.replace(/\.pdf$/i, '');
    setUploadFilename(`${base} - Additional.pdf`);
    filenameInputRef.current?.focus();
    filenameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function loadFiles(y) {
    if (!y) { setFiles([]); return; }
    setLoading(true); setError('');
    fetch(`${API}/legacy-archive/${y}`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => r.json())
      .then(data => Array.isArray(data) ? setFiles(data) : setError(data.error || 'Failed to load files'))
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }

  function handleYearSubmit(e) {
    e.preventDefault();
    setMessage('');
    loadFiles(year);
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!year || !uploadFile) return;
    setUploading(true); setError(''); setMessage('');
    const form = new FormData();
    form.append('file', uploadFile);
    if (uploadFilename.trim()) form.append('filename', uploadFilename.trim());
    try {
      const res = await fetch(`${API}/legacy-archive/${year}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: form,
      });
      const j = await res.json();
      if (res.ok) {
        setMessage(`${j.replaced ? 'Replaced' : 'Added'} "${j.filename}". It'll be searchable once the document indexer is next re-run.`);
        setUploadFile(null);
        setUploadFilename('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadFiles(year);
      } else {
        setError(j.error || 'Upload failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(filename) {
    if (!window.confirm(`Remove "${filename}" from ${year}'s Legacy Archive? This cannot be undone.`)) return;
    setError(''); setMessage('');
    try {
      const res = await fetch(`${API}/legacy-archive/${year}/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const j = await res.json();
      if (res.ok) {
        setMessage(`Removed "${filename}".`);
        loadFiles(year);
      } else {
        setError(j.error || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  }

  return (
    <div style={S.card}>
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Legacy Archive — manage individual documents</h2>
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        Add or remove individual PDF documents in the pre-2012 Legacy Archive — e.g.
        replacing a poor scan with a clean manual transcription. PDF only for now.
      </p>
      <form onSubmit={handleYearSubmit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div>
          <label style={S.label}>Year</label>
          <input style={{ ...S.input, width: 120 }} type="number" value={year}
            onChange={e => setYear(e.target.value)} placeholder="1955" required />
        </div>
        <button style={{ ...S.btn, ...S.btnSecondary }} type="submit">Load</button>
      </form>

      {message && <div style={S.success}>{message}</div>}
      {error && <div style={S.error}>{error}</div>}

      {year && (
        <>
          {loading && <div style={{ color: '#888', marginTop: 12 }}>Loading…</div>}
          {!loading && (
            <table style={{ ...S.table, marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={S.th}>File</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {files.length === 0 && (
                  <tr><td style={S.td} colSpan={2}>No documents for {year} yet.</td></tr>
                )}
                {files.map(f => (
                  <tr key={f}>
                    <td style={S.td}>
                      <a href={`/api/results/legacy-archive/${year}/${encodeURIComponent(f)}`} target="_blank" rel="noopener noreferrer">{f}</a>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...S.btnSmall, ...S.btnSecondary, marginRight: 6 }} onClick={() => startAddCompanion(f)}>Add document</button>
                      <button style={{ ...S.btnSmall, ...S.btnSecondary, marginRight: 6 }} onClick={() => startReplace(f)}>Replace</button>
                      <button style={{ ...S.btnSmall, background: '#fdecea', color: '#c0392b' }} onClick={() => handleDelete(f)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <form onSubmit={handleUpload} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eef0f3' }}>
            <label style={S.label}>Add / replace a document</label>
            <input ref={fileInputRef} type="file" accept="application/pdf"
              onChange={e => setUploadFile(e.target.files?.[0] || null)} required />
            <label style={S.label}>Filename (optional — defaults to the uploaded file&rsquo;s name. Matching an existing filename REPLACES it; any other name ADDS a new document. Use the buttons above to pre-fill either.)</label>
            <input ref={filenameInputRef} style={S.input} type="text" value={uploadFilename}
              onChange={e => setUploadFilename(e.target.value)} placeholder="1 Trengwainton 11th April 1955.pdf" />
            <button style={{ ...S.btn, ...S.btnPrimary, marginTop: 12 }} type="submit" disabled={uploading || !uploadFile}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default function ResultsAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Arriving via a magic link, ignore any saved session until the link's
  // token is verified: otherwise the season list loads with the old cookie,
  // and if that session has expired the 401 logs out — wiping the new
  // session the link is creating at the same moment.
  const arrivingWithToken = searchParams.has('token');
  const [sessionToken, setSessionToken] = useState(() => arrivingWithToken ? null : getCookie('results_admin_session'));
  const [userEmail, setUserEmail] = useState(() => arrivingWithToken ? null : getCookie('results_admin_email'));
  const [verifying, setVerifying] = useState(arrivingWithToken);
  const [authError, setAuthError] = useState('');

  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [freezing, setFreezing] = useState(null); // year currently being frozen
  const [freezeMessage, setFreezeMessage] = useState('');
  const [expandedYear, setExpandedYear] = useState(null);

  const verifyRanRef = useRef(false);
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) return;
    if (verifyRanRef.current) return;
    verifyRanRef.current = true;
    setVerifying(true);
    fetch(`${API}/verify?token=${token}`)
      .then(r => r.json())
      .then(j => {
        if (j.session_token) {
          setCookie('results_admin_session', j.session_token, 90);
          setCookie('results_admin_email', j.email, 90);
          setSessionToken(j.session_token);
          setUserEmail(j.email);
          setSearchParams({}, { replace: true });
        } else {
          setAuthError(j.error || 'Login failed – please request a new link.');
        }
      })
      .catch(() => setAuthError('Network error during login.'))
      .finally(() => setVerifying(false));
  }, []);

  function loadSeasons() {
    if (!sessionToken) return;
    setLoading(true); setListError('');
    fetch(`${API}/seasons`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => { if (r.status === 401) { handleLogout(); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        if (Array.isArray(data)) setSeasons(data);
        else setListError(data.error || 'Failed to load seasons');
      })
      .catch(() => setListError('Network error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadSeasons(); }, [sessionToken]);

  function handleLogout() {
    if (sessionToken) {
      fetch(`${API}/logout`, { method: 'POST', headers: { Authorization: `Bearer ${sessionToken}` } }).catch(() => {});
    }
    deleteCookie('results_admin_session');
    deleteCookie('results_admin_email');
    setSessionToken(null);
    setUserEmail(null);
  }

  async function handleFreeze(season) {
    const { year } = season;
    const what = season.jasper
      ? `archives ${year}'s Jasper results and championship PDFs`
      : `renders ${year}'s results and championship pages to PDF`;
    if (!window.confirm(
      season.frozen
        ? `${year} already has zero live results-hub links. Freeze again anyway? This only archives anything that's been added since — it never touches or replaces an already-archived document.`
        : `Freeze ${year}? Do this once the AGM has approved ${year}'s results. It ${what}, for whatever isn't archived yet — anything already covered is left untouched. Frozen documents can't be undone from here.`
    )) return;
    setFreezing(year); setFreezeMessage('');
    // Each call freezes for up to ~40s and reports what's left (see
    // FREEZE_TIME_BUDGET_SECONDS); keep calling until nothing remains or a
    // call makes no progress.
    let generated = 0;
    let errors = [];
    try {
      for (;;) {
        const res = await fetch(`${API}/seasons/${year}/freeze`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        const j = await res.json();
        if (!res.ok) {
          setFreezeMessage(`${year}: ${j.error || 'Failed to freeze'}${generated ? ` (after ${generated} document(s) archived)` : ''}`);
          break;
        }
        generated += j.documents_generated;
        errors = j.errors || [];
        if (j.remaining > 0 && j.documents_generated > 0) {
          setFreezeMessage(`${year}: ${generated} document(s) archived, ${j.remaining} to go…`);
          continue;
        }
        setFreezeMessage(`${year}: archived ${generated} document(s)${errors.length ? `, ${errors.length} failed — Freeze again to retry: ${errors.join('; ')}` : ''}.`);
        break;
      }
    } catch {
      setFreezeMessage(`${year}: network error${generated ? ` (after ${generated} document(s) archived — Freeze again to carry on)` : ''}`);
    } finally {
      setFreezing(null);
      loadSeasons();
    }
  }

  if (verifying) return <div style={{ ...S.page, padding: '2rem', textAlign: 'center' }}>Verifying login…</div>;
  if (!sessionToken) {
    return (
      <>
        {authError && <div style={{ ...S.error, maxWidth: 420, margin: '2rem auto' }}>{authError}</div>}
        <LoginScreen />
      </>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.headerTitle}>🏁 Results Admin</h1>
          <div style={S.headerMeta}>{userEmail}</div>
        </div>
        <button style={{ ...S.btn, ...S.btnSecondary }} onClick={handleLogout}>Log out</button>
      </div>

      <div style={S.wrap}>
        <div style={S.card}>
          <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Freeze season results to PDF</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            Once the AGM has approved a season&rsquo;s results, freeze it here so its
            results and standings are pinned to dated PDFs and can&rsquo;t silently
            change if the scoring pipeline evolves later. The results hub then links
            to the frozen PDFs. {LAST_JASPER_YEAR} archives Jasper&rsquo;s own reports;
            later seasons render this site&rsquo;s results pages.
          </p>
          {freezeMessage && <div style={S.success}>{freezeMessage}</div>}
          {loading && <div style={{ color: '#888' }}>Loading seasons…</div>}
          {listError && <div style={S.error}>{listError}</div>}

          {!loading && !listError && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Season</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Frozen</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {seasons.map(s => (
                  <Fragment key={s.year}>
                    <tr>
                      <td style={S.td}><strong>{s.year}</strong></td>
                      <td style={S.td}>
                        <span style={{ ...S.badge, background: s.frozen ? '#eafaf1' : '#fdecea', color: s.frozen ? '#1e8449' : '#c0392b' }}>
                          {s.frozen ? 'Frozen' : `${s.gap_count} to freeze`}
                        </span>
                      </td>
                      <td style={S.td}>{s.frozen_at ? `${fmtDateTime(s.frozen_at)} by ${s.frozen_by}` : '—'}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <button
                          style={{ ...S.btnSmall, ...S.btnPrimary, marginRight: 6, ...(!s.can_freeze ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                          disabled={freezing !== null || !s.can_freeze}
                          onClick={() => handleFreeze(s)}
                          title={s.freeze_blocked_reason || undefined}
                        >
                          {freezing === s.year ? 'Freezing…' : 'Freeze'}
                        </button>
                        <button
                          style={{ ...S.btnSmall, ...S.btnSecondary }}
                          onClick={() => setExpandedYear(expandedYear === s.year ? null : s.year)}
                        >
                          {expandedYear === s.year ? 'Hide' : 'Documents'}
                        </button>
                      </td>
                    </tr>
                    {expandedYear === s.year && (
                      <tr>
                        <td style={{ ...S.td, background: '#fafbfc' }} colSpan={4}>
                          <SeasonDocuments year={s.year} sessionToken={sessionToken} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <LegacyArchiveManager sessionToken={sessionToken} />
      </div>
    </div>
  );
}
